import { Set, List, Map, fromJS } from 'immutable'
import { combineReducers } from 'redux-immutable'
import { handleActions, combineActions } from 'redux-actions'
import { values } from 'lodash'

import { weakUuid } from './helpers'
import * as models from './models'
import {
  CtaGoalTypes,
  ConditionTypes,
  AudienceType,
  AnswerType,
  ValidationType,
  PlaybookStatus,
} from './constants'
import * as actions from './actions'
import * as ProfileActions from 'app/modules/profile/actions'

const updateEntities = model => entities => {
  return entities ? entities.map(entity => new model(entity)) : Map()
}

const sendTestEmailStatus = handleActions(
  {
    [combineActions(
      actions.sendTestEmailSuccess,
      actions.sendTestEmailPending,
      actions.sendTestEmailFailed
    )]: (state, action) => action.type,
  },
  false
)

const sendTestEmailId = handleActions(
  {
    [actions.sendTestEmail]: (state, action) => action.payload.emailId || false,
  },
  false
)

const endUserEmail = handleActions({
  [actions.setEndUserEmail]: (state, action) => action.payload
}, '')

const enrolledPlaybookId = handleActions(
  {
    [actions.fetchEnrolledPlaybookIdSuccess]: (state, action) => action.payload,
    [actions.unenrollSuccess]: () => false,
  },
  false
)

const normalized = handleActions(
  {
    [actions.searchPlaybooksSuccess]: (state, action) => {
      const processedEntities = action.payload
        .updateIn(['entities', 'playbooks'], updateEntities(models.Playbook))
        .updateIn(['entities', 'conditionSets'], updateEntities(models.ConditionSet))
        .updateIn(['entities', 'conditions'], updateEntities(models.Condition))

      action.payload
        .get('result')
        .map(playbookId =>
          processedEntities.updateIn(['entities', 'playbooks', `${playbookId}`, 'targeting'], targeting =>
            models.Targeting(targeting)
          )
        )

      // mergeDeep doesn't concat Lists properly...
      return state.mergeDeep(processedEntities).updateIn(['result'], result =>
        result
          .concat(action.payload.get('result'))
          .toSet()
          .toList()
      )
    },
    [actions.setStatus]: (state, action) => {
      const playbookId = action.payload.id
      const status = action.payload.status
      return state.setIn(['entities', 'playbooks', `${playbookId}`, 'status'], status)
    },
  },
  Map()
)

const normalizedStatus = handleActions(
  {
    [combineActions(
      actions.searchPlaybooksPending,
      actions.searchPlaybooks,
      actions.searchPlaybooksSuccess,
      actions.searchPlaybooksFailed
    )]: (state, action) => action.type,
  },
  false
)

const enrolledSequenceCount = handleActions(
  {
    [actions.fetchEnrolledSequenceCountSuccess]: (state, action) => action.payload,
  },
  Map()
)

const fetchEnrolledSequenceCountStatus = handleActions(
  {
    [combineActions(
      actions.fetchEnrolledSequenceCountSuccess,
      actions.fetchEnrolledSequenceCount,
      actions.fetchEnrolledSequenceCountPending,
      actions.fetchEnrolledSequenceCountFailed
    )]: (state, action) => action.type,
  },
  false
)

const enrollingPlaybookId = handleActions(
  {
    [actions.enroll]: (state, action) => action.payload.playbookId,
    [combineActions(actions.enrollFailed, actions.enrollSuccess, actions.clearEnrollModalState)]: () => false,
  },
  false
)

const editingPlaybookId = handleActions(
  {
    [actions.fetchPlaybookForEndUser]: (state, action) => action.payload.playbookId,
    [combineActions(
      actions.fetchPlaybookForEndUserSuccess,
      actions.fetchPlaybookForEndUserFailed,
      actions.clearEnrollModalState
    )]: () => false,
  },
  false
)

const playbookForEndUser = handleActions(
  {
    [actions.fetchPlaybookForEndUserSuccess]: (state, action) => action.payload,
    [actions.setPlaybookForEndUserSubject]: (state, action) => {
      const { index, subject } = action.payload
      return state.setIn(['actions', 0, 'action', 'emails', index, 'title'], subject)
    },
    [actions.setPlaybookForEndUserMessage]: (state, action) => {
      const { index, message } = action.payload
      return state.setIn(['actions', 0, 'action', 'emails', index, 'message'], message)
    },
    [actions.clearEnrollModalState]: (state, action) => {
      return false
    },
    [actions.setPlaybookForEndUserDelay]: (state, action) => {
      const delay = action.payload
      return state.setIn(['actions', 0, 'action', 'emails', 0, 'delay'], delay)
                .setIn(['actions', 0, 'action', 'emails', 0, 'smartSendingOverride'], true)
                .setIn(['actions', 0, 'action', 'emails', 0, 'delayUnit'], 'MILLIS')
    },
  },
  false
)

const enrollStatusByEmail = handleActions(
  {
    [combineActions(actions.enroll, actions.enrollFailed, actions.enrollSuccess, actions.enrollPending)]: (
      state,
      action
    ) => state.set(action.payload.email, action.type),
    [actions.clearEnrollModalState]: (state, action) => {
      return Map()
    },
  },
  Map()
)

const enrollErrorsByEmail = handleActions(
  {
    [actions.enrollFailed]: (state, action) => {
      return state.set(action.payload.email, action.payload.error)
    },
    [actions.clearEnrollModalState]: (state, action) => {
      return Map()
    },
  },
  Map()
)

const fetchPlaybookForEndUserStatus = handleActions(
  {
    [combineActions(
      actions.fetchPlaybookForEndUser,
      actions.fetchPlaybookForEndUserSuccess,
      actions.fetchPlaybookForEndUserPending,
      actions.fetchPlaybookForEndUserFailed
    )]: (state, action) => action.type,
  },
  false
)

const draft = handleActions(
  {
    [actions.clearDraft]: () => Map(),
    [actions.acceptPlaybookDraft]: (state, action) => {
      return action.payload
        .updateIn(['entities', 'signatureActions'], updateEntities(models.Signature))
        .updateIn(['entities', 'questions'], updateEntities(models.Question))
        .updateIn(['entities', 'answers'], updateEntities(models.Answer))
        .updateIn(['entities', 'keywords'], updateEntities(models.Keyword))
        .updateIn(['entities', 'emails'], updateEntities(models.Email))
        .updateIn(['entities', 'playbooks'], updateEntities(models.Playbook))
        .updateIn(['entities', 'goals'], updateEntities(models.Goal))
        .updateIn(['entities', 'conditionSets'], updateEntities(models.ConditionSet))
        .updateIn(['entities', 'conditions'], updateEntities(models.Condition))
        .updateIn(['entities', 'playbooks', `${action.payload.get('result')}`, 'targeting'], targeting =>
          models.Targeting(targeting)
        )
    },
    [actions.setMetaAttribute]: (state, action) => {
      // expects { attr: value }, will be masked by the PlaybookMeta Record()
      // also, merging records is sticky
      const draftId = state.get('result')
      const oldMeta = state.getIn(['entities', 'playbooks', `${draftId}`, 'meta'])
      return state.setIn(
        ['entities', 'playbooks', `${draftId}`, 'meta'],
        models.PlaybookMeta({ ...oldMeta, ...action.payload })
      )
    },
    [actions.setName]: (state, action) => {
      const draftId = state.get('result')
      return state.setIn(['entities', 'playbooks', `${draftId}`, 'name'], action.payload)
    },
    [actions.setDraftStatus]: (state, action) => {
      const playbookId = state.get('result')
      const status = action.payload.status
      return state.setIn(['entities', 'playbooks', `${playbookId}`, 'status'], status)
    },
    [actions.disablePlaybook]: state => {
      const draftId = state.get('result')
      return state.setIn(['entities', 'playbooks', `${draftId}`, 'status'], PlaybookStatus.DISABLED)
    },
    [actions.setSlug]: (state, action) => {
      const interactionId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      const slug = action.payload
        .toLowerCase()
        .replace(' ', '')
        .replace('/', '')
      return state.setIn(['entities', 'actionTypes', `${interactionId}`, 'action', 'slug'], slug)
    },
    [actions.setGreeting]: (state, action) => {
      const interactionId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      const { greeting } = action.payload
      return state.setIn(['entities', 'actionTypes', `${interactionId}`, 'action', 'greeting'], greeting)
    },
    [actions.setMarketingEmailLayout]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'options', 'marketingEmailLayout'],
        action.payload
      )
    },
    [actions.setSequenceDynamicSenderFormat]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'options', 'dynamicSenderFormat'],
        action.payload
      )
    },
    [actions.setSequenceDynamicAccountSendAttribute]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'options', 'dynamicAccountSendAttribute'],
        action.payload
      )
    },
    [actions.setSequenceEmailOverride]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'options', 'senderEmailOverride'],
        action.payload
      )
    },
    [actions.setSequenceIndividualSenderName]: (state, action) => {
      const { data, emailId } = action.payload
      return state.setIn(
        ['entities', 'emails', `${emailId}`, 'sendFromOverride'],
        data
      )
    },
    [actions.setSequenceIndividualEmailOverride]: (state, action) => {
      const { data, emailId } = action.payload
      return state.setIn(
        ['entities', 'emails', `${emailId}`, 'senderEmailOverride'],
        data
      )
    },
    [actions.setSequenceIndividualDynamicAttr]: (state, action) => {
      const { data, emailId } = action.payload
      return state.setIn(
        ['entities', 'emails', `${emailId}`, 'dynamicAccountSendAttribute'],
        data
      )
    },
    [actions.setSequenceIndividualDynamicFormat]: (state, action) => {
      const { data, emailId } = action.payload
      return state.setIn(
        ['entities', 'emails', `${emailId}`, 'dynamicSenderFormat'],
        data
      )
    },
    [actions.setSequenceSenderName]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'options', 'sendFromOverride'],
        action.payload
      )
    },
    [actions.setDynamicSenderFormat]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'dynamicSenderFormat'],
        action.payload
      )
    },
    [actions.setDynamicAccountSendAttribute]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'dynamicAccountSendAttribute'],
        action.payload
      )
    },
    [actions.setEmailOverride]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'senderEmailOverride'],
        action.payload
      )
    },
    [actions.setSenderName]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'sendFromOverride'],
        action.payload
      )
    },
    [actions.setActionInboxId]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(['entities', 'actionTypes', `${actionTypeId}`, 'action', 'inboxId'], action.payload)
    },
    [actions.setMessage]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(['entities', 'actionTypes', `${actionTypeId}`, 'action', 'message'], action.payload)
    },
    [actions.setVideoUrl]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(['entities', 'actionTypes', `${actionTypeId}`, 'action', 'videoUrl'], action.payload)
    },
    [actions.setThankYouMessage]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'thankYouMessage'],
        action.payload
      )
    },
    [actions.setSequenceAuthorId]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'options', 'authorId'],
        action.payload
      )
    },
    [actions.setAuthorId]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(['entities', 'actionTypes', `${actionTypeId}`, 'action', 'authorId'], action.payload)
    },
    [actions.setHeadline]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(['entities', 'actionTypes', `${actionTypeId}`, 'action', 'title'], action.payload.value)
    },
    [actions.setThankYouHeadline]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'thankYouHeadline'],
        action.payload
      )
    },
    // these CTA settings should be merged, I think. I'd rather say setCta({ type: 'foo' }), I think...
    // this would alleviate boilerplate null checks when a cta does not exist...
    [actions.setCtaType]: (state, action) => {
      const actionType = state.getIn(['entities', 'actionTypes']).first()
      const actionTypeId = actionType.get('id')
      let stateWithCta = state
      if (actionType.getIn(['action', 'cta']) === null) {
        stateWithCta = stateWithCta.setIn(
          ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'cta'],
          models.Cta()
        )
      }
      return stateWithCta
        .setIn(['entities', 'actionTypes', `${actionTypeId}`, 'action', 'cta', 'type'], action.payload)
        .setIn(
        [
          'entities',
          'actionTypes',
          `${actionTypeId}`,
          'action',
          'goalTriggers',
          '0',
          'conditions'
        ],
        [[{
          value: CtaGoalTypes[action.payload],
          action: 'CAMPAIGN_EVENT',
          operator: 'OCCURRED',
        }]]
        )
    },
    [actions.setCtaLink]: (state, action) => {
      const actionType = state.getIn(['entities', 'actionTypes']).first()
      const actionTypeId = actionType.get('id')
      if (actionType.getIn(['action', 'cta']) === null) {
        return state.setIn(
          ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'cta'],
          models.Cta({ url: action.payload })
        )
      }
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'cta', 'url'],
        action.payload
      )
    },
    [actions.setCtaCopy]: (state, action) => {
      const actionType = state.getIn(['entities', 'actionTypes']).first()
      const actionTypeId = actionType.get('id')
      if (actionType.getIn(['action', 'cta']) === null) {
        return state.setIn(
          ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'cta'],
          models.Cta({ copy: action.payload })
        )
      }
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'cta', 'copy'],
        action.payload
      )
    },
    [actions.setEmailTheme]: (state, action) => {
      const emailTheme = action.payload
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(['entities', 'actionTypes', `${actionTypeId}`, 'action', 'emailTheme'], emailTheme)
    },
    [actions.setEmailMessage]: (state, action) => {
      const { message, emailId } = action.payload
      return state.setIn(['entities', 'emails', `${emailId}`, 'message'], message)
    },
    [actions.setEmailTitle]: (state, action) => {
      const { title, emailId } = action.payload
      return state.setIn(['entities', 'emails', `${emailId}`, 'title'], title)
    },
    [actions.setSmartSending]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'options', 'smartSending'],
        action.payload
      )
    },
    [actions.setWeekdaysOnly]: (state, action) => {
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state.setIn(
        ['entities', 'actionTypes', `${actionTypeId}`, 'action', 'options', 'weekdaysOnly'],
        action.payload
      )
    },
    [actions.setUnenrollOnReply]: (state, action) => {
      const draftId = state.get('result')
      return state.setIn(
        ['entities', 'playbooks', `${draftId}`, 'targeting', 'unenrollOnReply'],
        action.payload
      )
    },
    [actions.setUnenrollOnReplyByDomain]: (state, action) => {
      const draftId = state.get('result')
      return state.setIn(
        ['entities', 'playbooks', `${draftId}`, 'targeting', 'unenrollOnReplyByDomain'],
        action.payload
      )
    },
    [actions.setThreadedWithPrevious]: (state, action) => {
      const { emailId, threadedWithPrevious } = action.payload
      return state.setIn(['entities', 'emails', `${emailId}`, 'threadedWithPrevious'], threadedWithPrevious)
    },
    [actions.setEmailDelay]: (state, action) => {
      const { delay, emailId } = action.payload
      return state.setIn(['entities', 'emails', `${emailId}`, 'delay'], delay)
    },
    [actions.addEmail]: (state, action) => {
      const { email } = action.payload
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state
        .setIn(['entities', 'emails', email.id], email)
        .updateIn(['entities', 'actionTypes', `${actionTypeId}`, 'action', 'emails'], emails =>
          emails.push(email.id)
        )
    },
    [actions.deleteEmail]: (state, action) => {
      const emailId = action.payload
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state
        .deleteIn(['entities', 'emails', emailId])
        .updateIn(['entities', 'actionTypes', `${actionTypeId}`, 'action', 'emails'], emails =>
          emails.filter(id => id !== emailId)
        )
    },
    [actions.moveEmailUp]: (state, action) => {
      const emailIndex = action.payload
      const actionTypeId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      const emailId = state.getIn([
        'entities',
        'actionTypes',
        `${actionTypeId}`,
        'action',
        'emails',
        emailIndex,
      ])
      return state.updateIn(['entities', 'actionTypes', `${actionTypeId}`, 'action', 'emails'], emails =>
        emails.delete(emailIndex).insert(emailIndex - 1, emailId)
      )
    },
    [actions.setConditionValue]: (state, action) => {
      const { id, value } = action.payload
      return state.setIn(['entities', 'conditions', `${id}`, 'value'], value)
    },
    [actions.setEmailPlaybookConditionValue]: (state, action) => {
      const { conditionId, playbook } = action.payload
      const campaignIds = values(
        // get the campaign IDs from playbooks that have emails
        playbook.getIn(['entities', 'emails'], Map()).map(e => e.get('campaignId'))
        .merge(
          // get the campaign IDs from playbooks that only have actions
          playbook.getIn(['entities', 'actionTypes'], Map()).map(at => at.getIn(['action', 'campaignId']))
        ).toJS())
        // filter out falsy values
        .filter(x => !!x)
      return state.updateIn(['entities', 'conditions', `${conditionId}`], condition => (
        condition
          .set('value', List(campaignIds.map(cid => `d_campaign=${cid}`)))
          .set('operator', 'isAnyOf')
          .set('field', 'urlQuery')
      ))
    },
    [actions.setConditionField]: (state, action) => {
      const { field, id } = action.payload
      const conditionType = ConditionTypes.get(field)
      return state.updateIn(['entities', 'conditions', `${id}`], condition => {
        return condition
          .set('field', field)
          .set('operator', conditionType.operators[0])
          .set('value', conditionType.defaultValue)
      })
    },
    [actions.setConditionOperator]: (state, action) => {
      const { operator, id } = action.payload
      return state.setIn(['entities', 'conditions', `${id}`, 'operator'], operator)
    },
    [actions.deleteCondition]: (state, action) => {
      const id = action.payload
      const conditionSet = state
        .getIn(['entities', 'conditionSets'])
        .find(({ conditions }) => conditions.some(condition => condition === id))
      return state.withMutations(_state => {
        _state.deleteIn(['entities', 'conditions', id])
        if (conditionSet.conditions.size === 1) {
          _state.deleteIn(['entities', 'conditionSets', conditionSet.id])
          _state.updateIn(
            ['entities', 'playbooks', `${_state.get('result')}`, 'targeting', 'display'],
            display => display.filter(id => id !== conditionSet.id)
          )
        } else {
          _state.updateIn(['entities', 'conditionSets', conditionSet.id, 'conditions'], conditions =>
            conditions.filter(condition => condition !== id)
          )
        }
        return _state
      })
    },
    [actions.addCondition]: (state, action) => {
      const { condition, conditionSetId } = action.payload
      return state
        .setIn(['entities', 'conditions', `${condition.id}`], condition)
        .updateIn(['entities', 'conditionSets', `${conditionSetId}`, 'conditions'], conditions =>
          conditions.push(condition.id)
        )
    },
    [actions.deleteConditionSet]: (state, action) => {
      const id = action.payload
      const playbookId = state.get('result')
      const removeConditionIds = Set(state.getIn(['entities', 'conditionSets', `${id}`, 'conditions']))
      return state
        .deleteIn(['entities', 'conditionSets', `${id}`])
        .updateIn(['entities', 'playbooks', `${playbookId}`, 'targeting', 'display'], display =>
          display.filter(conditionSet => conditionSet !== id)
        )
        .updateIn(['entities', 'conditions'], conditions =>
          conditions.filterNot(condition => removeConditionIds.has(condition.id))
        )
    },
    [actions.addConditionSet]: (state, action) => {
      const { conditionSet } = action.payload
      const playbookId = state.get('result')
      return state
        .setIn(['entities', 'conditionSets', `${conditionSet.id}`], conditionSet)
        .updateIn(['entities', 'playbooks', `${playbookId}`, 'targeting', 'display'], display =>
          display.push(conditionSet.id)
        )
    },
    [actions.addGoal]: (state, action) => {
      const { playbookId, goal } = action.payload
      return state
        .setIn(['entities', 'goals', goal.id], goal)
        .updateIn(['entities', 'playbooks', `${playbookId}`, 'goals'], goals => goals.push(goal.id))
    },
    [actions.saveGoalAction]: (state, action) => {
      const { goalId, goalAction } = action.payload
      const goal = state.getIn(['entities', 'goals', `${goalId}`])
      return state.setIn(['entities', 'goals', `${goalId}`], goal.merge(goalAction))
    },
    [actions.saveGoalRoutingOption]: (state, action) => {
      const { goalId, goalRoutingOption } = action.payload
      const goal = state.getIn(['entities', 'goals', `${goalId}`])
      return state.setIn(
        ['entities', 'goals', `${goalId}`],
        goal.merge({ routing: goalRoutingOption.type ? goalRoutingOption : null })
      )
    },
    [actions.deleteGoal]: (state, action) => {
      const { goalId, playbookId } = action.payload
      return state
        .deleteIn(['entities', 'goals', goalId])
        .updateIn(['entities', 'playbooks', `${playbookId}`, 'goals'], goals => {
          return goals.filter(id => id !== goalId)
        })
        .updateIn(['entities', 'answers'], answers => {
          if (!answers) {return}
          return answers.map(answer => {
            return answer.update('skipToGoal', skipToGoal => {
              if (skipToGoal) {
                if (skipToGoal.get('goalId') === goalId) {
                  return null
                } else {
                  return skipToGoal
                }
              } else {
                return null
              }
            })
          })
        })
    },
    [actions.removeGoalAction]: (state, action) => {
      const { goalId, goalActionProperty } = action.payload
      return state.deleteIn(['entities', 'goals', `${goalId}`, goalActionProperty])
    },
    [actions.setGoalMessage]: (state, action) => {
      const { goalId, body } = action.payload
      return state.setIn(['entities', 'goals', `${goalId}`, 'message', 'body'], body)
    },
    [actions.setGoalClassification]: (state, action) => {
      const { classificationId, goalId } = action.payload
      return state.setIn(
        ['entities', 'goals', `${goalId}`, 'classification', 'classificationId'],
        classificationId
      )
    },
    [actions.setQuestionPrompt]: (state, action) => {
      const { prompt, questionId } = action.payload
      return state.setIn(['entities', 'questions', `${questionId}`, 'prompt'], prompt)
    },
    [actions.addAnswer]: (state, action) => {
      const { answer } = action.payload
      return state
        .setIn(['entities', 'answers', answer.id], answer)
        .updateIn(['entities', 'questions', answer.questionId, 'answers'], answers => {
          return answers.push(answer.id)
        })
    },
    [actions.flagAnswer]: (state, action) => {
      const { answerId, questionId } = action.payload
      return state.setIn(['entities', 'questions', questionId, 'defaultAnswerId'], answerId)
    },
    [actions.setSkipTo]: (state, action) => {
      const { answerId, isGoal, skipToId } = action.payload
      return state
        .setIn(['entities', 'answers', answerId, 'skipTo'], isGoal ? null : skipToId)
        .setIn(['entities', 'answers', answerId, 'skipToGoal'], isGoal ? Map({ goalId: skipToId }) : null)
    },
    [actions.deleteAnswer]: (state, action) => {
      const { questionId, answerId } = action.payload
      return state
        .deleteIn(['entities', 'answers', answerId])
        .updateIn(['entities', 'questions', questionId, 'answers'], answers => {
          return answers.filter(id => id !== answerId)
        })
    },
    [actions.setDraftFrequency]: (state, action) => {
      const draftId = state.get('result')
      return state.setIn(
        ['entities', 'playbooks', `${draftId}`, 'targeting', 'campaignReenrollmentType'],
        action.payload
      )
    },
    [actions.addQuestion]: (state, action) => {
      const { question } = action.payload
      const interactionId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state
        .setIn(['entities', 'questions', question.id], question)
        .updateIn(['entities', 'actionTypes', `${interactionId}`, 'action', 'questions'], questions =>
          questions.push(question.id)
        )
    },
    [actions.addPhoneCaptureQuestion]: (state, action) => {
      const questionId = action.payload
      const interactionId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')

      const matchAnswer = models.Answer({
        id: weakUuid(),
        response: 'Got it! Thanks for entering your phone number.',
        type: AnswerType.VALIDATE,
        validationType: ValidationType.PHONE,
        questionId: questionId,
      })

      const nonMatchAnswerId = weakUuid()
      const nonMatchAnswer = models.Answer({
        id: nonMatchAnswerId,
        response: `Sorry, I didn't understand that. Please enter your phone number to continue.`,
        skipTo: questionId,
        questionId: questionId,
      })

      const question = models.Question({
        id: questionId,
        interactionId,
        prompt: `What's your phone number?`,
        defaultAnswerId: nonMatchAnswerId,
        answers: List.of(matchAnswer.id, nonMatchAnswer.id),
        fieldConfiguration: models.FieldConfiguration({ fieldName: 'phone' }),
      })

      return state
        .setIn(['entities', 'answers', matchAnswer.id], matchAnswer)
        .setIn(['entities', 'answers', nonMatchAnswer.id], nonMatchAnswer)
        .setIn(['entities', 'questions', question.id], question)
        .updateIn(
          ['entities', 'actionTypes', `${question.interactionId}`, 'action', 'questions'],
          questions => questions.push(question.id)
        )
    },
    [actions.addEmailCaptureQuestion]: (state, action) => {
      const questionId = action.payload
      const interactionId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')

      const matchAnswer = models.Answer({
        id: weakUuid(),
        response: 'Got it! Thanks for entering your email address.',
        type: AnswerType.VALIDATE,
        validationType: ValidationType.EMAIL,
        questionId: questionId,
      })

      const nonMatchAnswerId = weakUuid()
      const nonMatchAnswer = models.Answer({
        id: nonMatchAnswerId,
        response: `Sorry, I didn't understand that. Please enter your email address to continue.`,
        skipTo: questionId,
        questionId: questionId,
      })

      const question = models.Question({
        id: questionId,
        interactionId,
        prompt: `What's your email?`,
        defaultAnswerId: nonMatchAnswerId,
        answers: List.of(matchAnswer.id, nonMatchAnswer.id),
        fieldConfiguration: models.FieldConfiguration({ fieldName: 'email' }),
      })

      return state
        .setIn(['entities', 'answers', matchAnswer.id], matchAnswer)
        .setIn(['entities', 'answers', nonMatchAnswer.id], nonMatchAnswer)
        .setIn(['entities', 'questions', question.id], question)
        .updateIn(
          ['entities', 'actionTypes', `${question.interactionId}`, 'action', 'questions'],
          questions => questions.push(question.id)
        )
    },
    [actions.deleteQuestion]: (state, action) => {
      const questionId = action.payload
      const interactionId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      return state
        .deleteIn(['entities', 'questions', questionId])
        .updateIn(['entities', 'actionTypes', `${interactionId}`, 'action', 'questions'], questions =>
          questions.filter(id => id !== questionId)
        )
        .updateIn(['entities', 'answers'], answers => {
          if (!answers) {return}
          return answers.map(answer => {
            return answer.update('skipTo', skipTo => (skipTo === questionId ? null : skipTo))
          })
        })
    },
    [actions.moveQuestionUp]: (state, action) => {
      const questionIndex = action.payload
      const interactionId = state
        .getIn(['entities', 'actionTypes'])
        .first()
        .get('id')
      const questionId = state.getIn([
        'entities',
        'actionTypes',
        `${interactionId}`,
        'action',
        'questions',
        questionIndex,
      ])
      return state.updateIn(
        ['entities', 'actionTypes', `${interactionId}`, 'action', 'questions'],
        questions => {
          return questions.delete(questionIndex).insert(questionIndex - 1, questionId)
        }
      )
    },
    [actions.addKeyword]: (state, action) => {
      const { keyword, answerId } = action.payload
      return state
        .setIn(['entities', 'keywords', keyword.id], keyword)
        .updateIn(['entities', 'answers', answerId, 'keywords'], keywords => keywords.push(keyword.id))
    },
    [actions.deleteKeyword]: (state, action) => {
      const keywordId = action.payload
      return state
        .deleteIn(['entities', 'keywords', keywordId])
        .updateIn(['entities', 'answers'], answers => {
          return answers.map(answer => {
            return answer.update('keywords', keywords => {
              return keywords.filter(keyword => keyword !== keywordId)
            })
          })
        })
    },
    [actions.setKeywordText]: (state, action) => {
      const { keywordId, text } = action.payload
      return state.setIn(['entities', 'keywords', keywordId, 'text'], text)
    },
    [actions.setKeywordType]: (state, action) => {
      const { keywordId, type } = action.payload
      return state.setIn(['entities', 'keywords', keywordId, 'type'], type)
    },
    [actions.setAnswerType]: (state, action) => {
      const { answerId, type } = action.payload
      return state.setIn(['entities', 'answers', answerId, 'type'], type)
    },
    [actions.setButtonText]: (state, action) => {
      const { answerId, buttonText } = action.payload
      return state.setIn(['entities', 'answers', answerId, 'buttonText'], buttonText)
    },
    [actions.setDefaultPhoneCountryCode]: (state, action) => {
      const { answerId, countryCode } = action.payload
      return state.setIn(
        ['entities', 'answers', answerId, 'validationConfig', 'phoneCountryCode'],
        countryCode
      )
    },
    [actions.setResponse]: (state, action) => {
      const { answerId, response } = action.payload
      return state.setIn(['entities', 'answers', answerId, 'response'], response)
    },
    [actions.setButtonOnlyResponses]: (state, action) => {
      const { questionId, value } = action.payload
      return state.setIn(['entities', 'questions', questionId, 'isButtonOnly'], value)
    },
    [actions.setFieldAttributeName]: (state, action) => {
      const { questionId, attribute } = action.payload
      return state.setIn(
        ['entities', 'questions', questionId, 'fieldConfiguration'],
        attribute ? models.FieldConfiguration({ fieldName: attribute }) : null
      )
    },
    [actions.resetDisplayConditions]: state => {
      const draftId = state.get('result')
      return state
        .setIn(['entities', 'playbooks', `${draftId}`, 'targeting', 'display'], List())
        .setIn(['entities', 'conditionSets'], Map())
        .setIn(['entities', 'conditions'], Map())
    },
    [actions.setAudience]: (state, action) => {
      const draftId = state.get('result')
      if (action.payload !== AudienceType.SEGMENT) {
        return state
          .setIn(['entities', 'playbooks', `${draftId}`, 'targeting', 'audience'], action.payload)
          .setIn(['entities', 'playbooks', `${draftId}`, 'targeting', 'userListIds'], List())
      }
      return state.setIn(['entities', 'playbooks', `${draftId}`, 'targeting', 'audience'], action.payload)
    },
    [actions.setDraftSchedule]: (state, action) => {
      const draftId = state.get('result')
      const dueAt = !action.payload
        ? null
        : action.payload
            .clone()
            .tz('UTC')
            .startOf('minute')
            .unix() * 1000
      return state.setIn(['entities', 'playbooks', `${draftId}`, 'targeting', 'dueAt'], dueAt)
    },
    [actions.setUserListIds]: (state, action) => {
      const draftId = state.get('result')
      return state.setIn(['entities', 'playbooks', `${draftId}`, 'targeting', 'userListIds'], action.payload)
    },
    [actions.setExcludedUserListIds]: (state, action) => {
      const draftId = state.get('result')
      return state.setIn(
        ['entities', 'playbooks', `${draftId}`, 'targeting', 'excludedUserListIds'],
        action.payload
      )
    },
    [actions.setAccountListIds]: (state, action) => {
      const draftId = state.get('result')
      return state.setIn(['entities', 'playbooks', `${draftId}`, 'targeting', 'accountListIds'], action.payload)
    },
    [actions.setExcludedAccountListIds]: (state, action) => {
      const draftId = state.get('result')
      return state.setIn(
        ['entities', 'playbooks', `${draftId}`, 'targeting', 'excludedAccountListIds'],
        action.payload
      )
    },
    [actions.setSmartFilterEngagedContacts]: (state, action) => {
      const draftId = state.get('result')
      const hasSmartFilters = !!state.getIn([
        'entities',
        'playbooks',
        `${draftId}`,
        'targeting',
        'smartFilters',
      ])
      if (!hasSmartFilters) {
        const filters = new models.SmartFilters({
          meetingScheduled: action.payload,
          salesSequenceEngaged: action.payload,
        })
        return state.setIn(['entities', 'playbooks', `${draftId}`, 'targeting', 'smartFilters'], filters)
      } else {
        return state
          .setIn(
            ['entities', 'playbooks', `${draftId}`, 'targeting', 'smartFilters', 'meetingScheduled'],
            action.payload
          )
          .setIn(
            ['entities', 'playbooks', `${draftId}`, 'targeting', 'smartFilters', 'salesSequenceEngaged'],
            action.payload
          )
      }
    },
    [actions.setSmartFilterChatEngagedContacts]: (state, action) => {
      const draftId = state.get('result')
      const hasSmartFilters = !!state.getIn([
        'entities',
        'playbooks',
        `${draftId}`,
        'targeting',
        'smartFilters',
      ])
      if (!hasSmartFilters) {
        const filters = new models.SmartFilters({
          chatEngaged: action.payload,
        })
        return state.setIn(['entities', 'playbooks', `${draftId}`, 'targeting', 'smartFilters'], filters)
      } else {
        return state
          .setIn(
            ['entities', 'playbooks', `${draftId}`, 'targeting', 'smartFilters', 'chatEngaged'],
            action.payload
          )
      }
    },
    [actions.setSmartFilters]: (state, action) => state.setIn(['entities', 'playbooks', `${state.get('result')}`, 'targeting', 'smartFilters'], action.payload),
    [actions.clearEmailBanner]: (state, action) => {
      const { payload: { emailId }} = action
      return state.setIn(['entities', 'emails', `${emailId}`, 'bannerImageUrl'], null)
    },
    [actions.setEmailBannerSuccess]: (state, action) => {
      const { payload: { res, emailId }} = action
      return state.setIn(['entities', 'emails', `${emailId}`, 'bannerImageUrl'], res.url)
    },
    [combineActions(
      actions.setSignatureIncludeAvatar,
      actions.setSignatureIncludeSocial,
      actions.setSignatureIncludeBanner,
      actions.setSignatureBody,
    )]: (state, action) => {
      let attribute = null

      switch (action.type) {
        case actions.setSignatureIncludeAvatar.toString():
          attribute = 'includeAvatar'
          break
        case actions.setSignatureIncludeSocial.toString():
          attribute = 'includeCompanySocialMedia'
          break
        case actions.setSignatureIncludeBanner.toString():
          attribute = 'hasCampaignGraphic'
          break
        case actions.setSignatureBody.toString():
          attribute = 'signatureBody'
          break
      }

      if (attribute === null)
        {return state}

      return state.updateIn(['entities', 'signatureActions'], sigs => sigs.map(sig => sig.set(attribute, action.payload)))
    },
    [combineActions(
      actions.setSignatureBannerSuccess,
      actions.setSignatureBannerLinkURL,
    )]: (state, action) => {
      const {
        signatureId,
        value,
      } = action.payload

      const attribute = action.type === actions.setSignatureBannerSuccess.toString() ? 'campaignGraphic' : 'campaignGraphicLink'

      return state.setIn(['entities', 'signatureActions', `${signatureId}`, `${attribute}`], value)
    },
    [actions.clearSignatureBanner]: (state, action) => {
      const signatureId = action.payload
      return state.setIn(['entities', 'signatureActions', `${signatureId}`, 'campaignGraphic'], null)
    },
    [actions.addNewSignatureToPlaybook]: state => {
      const signatureId = weakUuid()
      const signature = state.getIn(['entities', 'signatureActions'], Map()).first() || models.Signature()
      const actionId = state.getIn(['entities', 'actionTypes']).first().get('id')
      const newSignature = { ...signature.toJS(), id: signatureId, signatureId: null, campaignGraphic: null, campaignGraphicLink: null }
      return state
        .setIn(['entities', 'signatureActions', signatureId], models.Signature(newSignature))
        .updateIn(['entities', 'actionTypes', `${actionId}`, 'action', 'signatureActions'], ids => ids.concat([signatureId]))
    },
    [actions.addEmptyBannerSignature]: state => {
      const signatureId = weakUuid()
      const signature = state.getIn(['entities', 'signatureActions'], Map()).first() || models.Signature()
      const actionId = state.getIn(['entities', 'actionTypes']).first().get('id')
      const newSignature = { ...signature.toJS(), id: signatureId, signatureId: null, campaignGraphic: null, campaignGraphicLink: null, hasCampaignGraphic: false }
      return state
        .setIn(['entities', 'signatureActions', signatureId], models.Signature(newSignature))
        .updateIn(['entities', 'actionTypes', `${actionId}`, 'action', 'signatureActions'], ids => ids.concat([signatureId]))
    },
    [actions.removeSignature]: (state, action) => {
      const signatureId = action.payload
      const actionId = state.getIn(['entities', 'actionTypes']).first().get('id')
      return state
        .updateIn(['entities', 'signatureActions'], sigs => sigs.filterNot(sig => sig.id === signatureId))
        .updateIn(['entities', 'actionTypes', `${actionId}`, 'action', 'signatureActions'], ids => ids.filter(id => id !== signatureId))
    },
    [actions.removeEmptyBannerSignature]: state => {
      const signatureHasNoBanner = sig => !sig.hasCampaignGraphic
      const sigWithoutBanner = (state.getIn(['entities', 'signatureActions'], Map()).filter(signatureHasNoBanner) || Map()).first()
      if (sigWithoutBanner) {
        const sigId = sigWithoutBanner.id
        const actionId = state.getIn(['entities', 'actionTypes']).first().get('id')
        return state
          .updateIn(['entities', 'signatureActions'], sigs => sigs.filterNot(sig => sig.id === sigId))
          .updateIn(['entities', 'actionTypes', `${actionId}`, 'action', 'signatureActions'], ids => ids.filter(id => id !== sigId))
      } else {
        return state
      }
    },
  },
  null
)

const status = handleActions(
  {
    [combineActions(
      actions.fetchPlaybook,
      actions.fetchPlaybookPending,
      actions.fetchPlaybookCancelled,
      actions.fetchPlaybookFailed,
      actions.savePlaybook,
      actions.savePlaybookPending,
      actions.savePlaybookCancelled,
      actions.savePlaybookSuccess,
      actions.savePlaybookFailed,
      actions.savePlaybookDraft,
      actions.savePlaybookDraftPending,
      actions.savePlaybookDraftCancelled,
      actions.savePlaybookDraftFailed,
      actions.receivePlaybookDraft,
      actions.acceptPlaybookDraft
    )]: (state, action) => {
      return action.type
    },
  },
  false
)

const ui = handleActions(
  {
    [combineActions(
      actions.fetchSignatureMultiBannerPreview,
      actions.fetchSignatureMultiBannerPreviewPending,
      actions.fetchSignatureMultiBannerPreviewFailed,
      actions.fetchSignatureMultiBannerPreviewSuccess,
    )]: (state, action) => state.setIn(['signatures', 'previewAll', 'status'], action.type),
    [combineActions(
      actions.fetchSignatureMultiBannerPreviewFailed,
      actions.fetchSignatureMultiBannerPreviewSuccess,
    )]: (state, action) => state.setIn(['signatures', 'previewAll', 'data'], fromJS(action.payload)),
    [actions.setViewingSignatureId]: (state, action) => state.set('signatures', 'viewing', action.payload.id),
    [actions.disablePlaybookAutosave]: state => state.set('autosave', false),
    [actions.enablePlaybookAutosave]: state => state.set('autosave', true),
    [actions.markDraftUnsaved]: state => state.set('saved', false).set('doPlaybookChanges', true),
    [actions.receivePlaybookDraft]: state => state.set('saved', true).set('doPlaybookChanges', false),
    [actions.setDraftStatusPending]: state => state.set('statusLoading', true),
    [combineActions(
      actions.setDraftStatusSuccess,
      actions.setDraftStatusCancelled,
      actions.setDraftStatusFailed
    )]: state => state.set('statusLoading', false),
    [combineActions(
      actions.sendTestSequenceSuccess,
      actions.sendTestSequencePending,
      actions.sendTestSequenceFailed,
      actions.sendTestSequenceCancelled,
    )]: (state, action) => state.set('sendTestSequenceStatus', action.type),
    [actions.fetchPlaybookCountsSuccess]: (state, { payload }) => {
      return state.set('counts', payload)
    },
    [combineActions(
      actions.savePlaybook,
      actions.savePlaybookDraft,
      actions.savePlaybookPending,
      actions.savePlaybookDraftPending
    )]: state => {
      return state.set('saving', true)
    },
    [actions.fetchPlaybookCardinalityPending]: state => {
      return state.set('cardinalityLoading', true)
    },
    [combineActions(
      actions.fetchPlaybookCardinalitySuccess,
      actions.fetchPlaybookCardinalityCancelled,
      actions.fetchPlaybookCardinalityFailed
    )]: state => {
      return state.set('cardinalityLoading', false)
    },
    [actions.fetchPlaybookCardinalitySuccess]: (state, action) => {
      return state.set('targetingCardinality', action.payload.cardinality)
    },
    [combineActions(
      actions.fetchDataPreviewEmailPending,
      actions.fetchDataPreviewSequenceEmailPending,
    )]: state => {
      return state.set('templateContactPreviewLoading', true)
    },
    [combineActions(
      actions.setSignatureBanner,
      actions.setSignatureBannerPending,
      actions.setSignatureBannerFailed,
      actions.setSignatureBannerSuccess,
      actions.setSignatureBannerCancelled,
    )]: (state, action) => state.set('signatureBannerUploadStatus', action.type),
    [actions.setEmailBannerPending]: (state, action) => {
      const { emailId } = action.payload
      return state.setIn(['bannerUploadPending', emailId], true)
    },
    [combineActions(
      actions.setEmailBannerSuccess,
      actions.setEmailBannerFailed,
    )]: (state, action) => {
      const { emailId } = action.payload
      return state.setIn(['bannerUploadPending', emailId], false)
    },
    [combineActions(
      actions.fetchDataPreviewEmailCancelled,
      actions.fetchDataPreviewEmailFailed,
      actions.fetchDataPreviewEmailSuccess,
      actions.fetchDataPreviewSequenceEmailCancelled,
      actions.fetchDataPreviewSequenceEmailFailed,
      actions.fetchDataPreviewSequenceEmailSuccess
    )]: state => {
      return state.set('templateContactPreviewLoading', false)
    },
    [combineActions(
      actions.savePlaybookDraftFailed,
      actions.savePlaybookDraftCancelled,
      actions.receivePlaybookDraft,
      actions.savePlaybookFailed,
      actions.savePlaybookSuccess,
      actions.savePlaybookCancelled
    )]: state => {
      return state.set('saving', false)
    },
    [combineActions(
      actions.fetchPlaybook,
      actions.createPlaybook,
      actions.fetchPlaybookPending,
      actions.searchPlaybooks,
      actions.searchPlaybooksPending
    )]: state => {
      return state.set('fetching', true)
    },
    [combineActions(
      actions.receivePlaybookDraft,
      actions.searchPlaybooksSuccess,
      actions.searchPlaybooksSuccess,
      actions.searchPlaybooksFailed
    )]: state => {
      return state.set('fetching', false)
    },
    [actions.fetchPlaybooksPrioritySuccess]: (state, action) => {
      const orderedIds = action.payload
        .sortBy(playbook => playbook.get('priority'))
        .map(playbook => playbook.get('id'))
        .toArray()
      return state.set('priorityOrderedPlaybookIds', orderedIds)
    },
    [actions.fetchDataPreviewEmailSuccess]: (state, action) =>
      state.setIn(['emailRenderedPreviews', action.payload.playbookId], action.payload.template),
    [actions.fetchDataPreviewSequenceEmailSuccess]: (state, action) =>
      state.setIn(['emailRenderedPreviews', action.payload.emailId], action.payload.template),
    [actions.setCurrentGoalId]: (state, action) => state.set('currentGoalId', action.payload),
    [actions.showSendEmailConfirmModal]: state => state.set('sendEmailConfirmModalVisible', true),
    [actions.hideSendEmailConfirmModal]: state => state.set('sendEmailConfirmModalVisible', false),
    [actions.showExtensionInviteModal]: state => state.set('extensionInviteModalVisible', true),
    [actions.hideExtensionInviteModal]: state => state.set('extensionInviteModalVisible', false),
    [combineActions(actions.hideGoalActionModal, actions.hideGoalRoutingModal)]: state =>
      state
        .set('selectedGoalAction', null)
        .set('goalActionModalVisible', false)
        .set('goalRoutingModalVisible', false),
    [actions.showGoalActionModal]: state => state.set('goalActionModalVisible', true),
    [actions.showGoalRoutingModal]: state => state.set('goalRoutingModalVisible', true),

    [actions.selectGoalAction]: (state, action) => state.set('selectedGoalAction', action.payload),
    [actions.selectGoalRoutingOptionUI]: (state, action) =>
      state.set('selectedGoalRoutingOption', action.payload),
    [actions.setGoalRoutingView]: (state, action) => state.set('goalRoutingView', action.payload),
    [actions.showAnswerModal]: (state, action) =>
      state.set('answerModalVisible', true).set('currentAnswerId', action.payload),
    [actions.showScheduleModal]: state => state.set('scheduleModalVisible', true),
    [actions.hideScheduleModal]: state => state.set('scheduleModalVisible', false),
    [actions.hideAnswerModal]: state => {
      return state.set('answerModalVisible', false).set('currentAnswerId', null)
    },
    [actions.showQuestionAdvancedModal]: (state, action) =>
      state.set('QuestionAdvancedModalVisible', true).set('currentQuestionId', action.payload),
    [actions.hideQuestionAdvancedModal]: state =>
      state.set('QuestionAdvancedModalVisible', false).set('currentQuestionId', null),
    [actions.showConditionModal]: (state, action) =>
      state.set('conditionModalVisible', true).set('currentConditionId', action.payload),
    [actions.hideConditionModal]: state =>
      state.set('conditionModalVisible', false).set('currentConditionId', null),
    [actions.showEditSenderModal]: (state, action) => state.setIn(['editSenderModal', 'visible'], true).setIn(['editSenderModal', 'editingEmailId'], action.payload),
    [actions.hideEditSenderModal]: state => state.setIn(['editSenderModal', 'visible'], false).setIn(['editSenderModal', 'editingEmailId'], null),
    [actions.showSequenceInviteModal]: state => state.set('sequenceInviteModalVisible', true),
    [actions.hideSequenceInviteModal]: state => state.set('sequenceInviteModalVisible', false),
    [actions.showTokenModal]: state => state.set('tokenModalVisible', true),
    [actions.hideTokenModal]: state => state.set('tokenModalVisible', false),
    [actions.showCtaButtonModal]: state => state.set('ctaButtonModalVisible', true),
    [actions.hideCtaButtonModal]: state => state.set('ctaButtonModalVisible', false),
    [actions.showCalendarLinkModal]: state => state.set('calendarLinkVisible', true),
    [actions.hideCalendarLinkModal]: state => state.set('calendarLinkVisible', false),
    [actions.showGoogleAuthModal]: state => state.set('googleAuthModalVisible', true),
    [actions.hideGoogleAuthModal]: state => state.set('googleAuthModalVisible', false),
    [actions.showDisableSalesSequenceModal]: state => state.set('disableSalesSequenceModalVisible', true),
    [actions.hideDisableSalesSequenceModal]: state => state.set('disableSalesSequenceModalVisible', false),
    [actions.showSegmentSelect]: state => state.set('segmentSelectVisible', true),
    [actions.hideSegmentSelect]: state => state.set('segmentSelectVisible', false),
    [ProfileActions.fetchUserProfilePending]: state => state.set('calendarProfileLoading', true),
    [combineActions(ProfileActions.fetchUserProfileSuccess, ProfileActions.fetchUserProfileFailed)]: state =>
      state.set('calendarProfileLoading', false),
    [combineActions(
      actions.getPlaybookByCampaignIdCancelled,
      actions.getPlaybookByCampaignIdFailed
    )]: (state, action) => {
      const { campaignId } = action.payload
      return state.setIn(['campaignIdToPlaybookIdMap', campaignId], null)
    },
    [actions.getPlaybookByCampaignIdPending]: (state, action) => {
      const { campaignId } = action.payload
      return state.setIn(['campaignIdToPlaybookIdMap', campaignId], 'PENDING')
    },
    [actions.getPlaybookByCampaignIdSuccess]: (state, action) => {
      const { playbook, campaignId } = action.payload
      return state.setIn(['campaignIdToPlaybookIdMap', campaignId], playbook.get('result').first())
    },
    [combineActions(
      actions.asyncButtonPressFailed,
      actions.asyncButtonPressPending,
      actions.asyncButtonPressSuccess,
    )]: (state, action) => {
      const payload = Map({
        at: action.payload.at,
        meta: action.payload.meta,
        status: action.type.toString()
      })
      return state.setIn(['asyncButtonPress', `${action.payload.clickId}`], payload)
    },
    [actions.setEditSenderLevel]: (state, action) => state.set('editSenderLevel', action.payload),
  },
  Map()
)

const byIds = handleActions(
  {
    [actions.fetchPlaybooksByIdsSuccess]: (state, action) => {
      const playbooksById = action.payload.reduce((acc, playbook) => acc.set(playbook.id, playbook), Map())
      return state.merge(playbooksById)
    },
    [actions.getPlaybookPending]: (state, action) => {
      const { playbookId } = action
      return state.set(playbookId, 'PENDING')
    },
    [actions.getPlaybookSuccess]: (state, action) => {
      const { payload } = action
      const playbookId = payload.get('result')
      return state.set(playbookId, payload.getIn(['entities', 'playbooks', playbookId]))
    }
  },
  Map()
)

const playbookReportTypesByGate = handleActions({
  [actions.fetchPlaybooksByGatedPlanFailed]: state => state.set('loading', true),
  [combineActions(
    actions.fetchPlaybooksByGatedPlanCancelled,
    actions.fetchPlaybooksByGatedPlanFailed,
  )]: state => state.set('loading', false),
  [actions.fetchPlaybooksByGatedPlanSuccess]: (state, action) => state.set('loading', false).set('types', Map(action.payload)).set('loaded', true)
}, Map())

const campaignIdToPlaybook = handleActions({
  [combineActions(
    actions.fetchPlaybookForCampaignIdPending,
    actions.fetchPlaybookForCampaignIdCancelled,
  )]: (state, action) => {
    const payload = state.get(action.payload.campaignId, Map()).merge(Map({
      status: action.type,
    }))
    return state.set(action.payload.campaignId, payload)
  },
  [actions.fetchPlaybookForCampaignIdSuccess]: (state, action) =>
    state
      .setIn([action.payload.campaignId, 'data'], action.payload.data)
      .setIn([action.payload.campaignId, 'status'], action.type),
  [actions.fetchPlaybookForCampaignIdFailed]: (state, action) =>
    state
      .setIn([action.payload.campaignId, 'err'], action.payload.err)
      .setIn([action.payload.campaignId, 'status'], action.type),
}, Map())

const playbooksByReportType = handleActions({
  [actions.fetchPlaybooksByReportTypeSuccess]: (state, action) => {
    return state.setIn(['data', action.payload.reportType, 'disabled'], action.payload.playbooks.filter(playbook => 'DISABLED' === playbook.status))
  }
}, Map())

export default combineReducers({
  status,
  normalized,
  draft,
  ui,
  normalizedStatus,
  sendTestEmailStatus,
  sendTestEmailId,
  enrolledPlaybookId,
  enrolledSequenceCount,
  fetchEnrolledSequenceCountStatus,
  playbookForEndUser,
  fetchPlaybookForEndUserStatus,
  enrollErrorsByEmail,
  enrollStatusByEmail,
  enrollingPlaybookId,
  editingPlaybookId,
  byIds,
  playbookReportTypesByGate,
  endUserEmail,
  campaignIdToPlaybook,
  playbooksByReportType,
})



// WEBPACK FOOTER //
// ./src/scripts/app/modules/playbook/reducer.js